/**
 * SourcePanelContext - controls the slide-in source panel + the
 * inline video-citation expansion, from anywhere in the dashboard
 * tree.
 *
 * Why one context for two surfaces:
 *   - We want exactly ONE source open at a time across the chat.
 *     Opening a doc panel closes any expanded video, and vice versa.
 *     A single shared target gives us that for free.
 *   - Trigger sites are deep inside <Message><MessageSources/>;
 *     prop-drilling open/close handlers through every Message would
 *     be noisy.
 *
 * Target shape is a discriminated union:
 *   - { kind: 'doc' } → SourcePanel slide-in renders.
 *   - { kind: 'video' } → MessageSources renders the matching
 *     VideoCitationTile in expanded mode, inline above the pill row.
 *
 * Legacy `open(docId, hint)` kept as an alias for `openDoc()` so the
 * older callers in MessageSources still compile while the new pill
 * UX rolls out.
 */
import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
  type ReactNode,
} from 'react';

interface DocTarget {
  kind: 'doc';
  docId: string;
  /** Title we know up-front (from the message's sources array) -
   *  shown immediately so the panel header doesn't say "Loading…".
   *  The fetched DocumentDetail's title overrides this once it
   *  arrives (admin friendly-name overlay may differ). */
  titleHint: string;
}

interface VideoTarget {
  kind: 'video';
  videoId: string;
}

export type SourcePanelTarget = DocTarget | VideoTarget;

interface SourcePanelContextValue {
  target: SourcePanelTarget | null;
  /** Open the doc slide-in panel. */
  openDoc: (docId: string, titleHint: string) => void;
  /** Legacy alias for openDoc - pre-PR callers still in flight. */
  open: (docId: string, titleHint: string) => void;
  /** Expand a video citation inline. The matching pill in
   *  MessageSources renders the player above the row. */
  openVideo: (videoId: string) => void;
  /** Close whatever's open. */
  close: () => void;
}

const SourcePanelContext = createContext<SourcePanelContextValue | null>(null);

export function SourcePanelProvider({ children }: { children: ReactNode }) {
  const [target, setTarget] = useState<SourcePanelTarget | null>(null);

  const openDoc = useCallback((docId: string, titleHint: string) => {
    setTarget({ kind: 'doc', docId, titleHint });
  }, []);
  const openVideo = useCallback((videoId: string) => {
    setTarget({ kind: 'video', videoId });
  }, []);
  const close = useCallback(() => {
    setTarget(null);
  }, []);

  // Stable identity → useMemo so consumers don't re-render every
  // time the parent does.
  const value = useMemo(
    () => ({ target, openDoc, open: openDoc, openVideo, close }),
    [target, openDoc, openVideo, close],
  );

  return (
    <SourcePanelContext.Provider value={value}>
      {children}
    </SourcePanelContext.Provider>
  );
}

export function useSourcePanel(): SourcePanelContextValue {
  const ctx = useContext(SourcePanelContext);
  if (!ctx) {
    throw new Error('useSourcePanel must be used inside <SourcePanelProvider>');
  }
  return ctx;
}
